import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Mic, MicOff, Volume2, VolumeX, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const LessonView = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const audioChat = useRef(null);

  useEffect(() => {
    checkSubscriptionAndLoadLesson();
  }, [lessonId]);

  const checkSubscriptionAndLoadLesson = async () => {
    try {
      // First check user's subscription status
      const accessRes = await axios.get(`${API}/lessons/access-status`, { withCredentials: true });
      const lessonAccess = accessRes.data.lesson_access || {};
      
      // Check if any lesson is accessible (meaning user has subscription)
      const hasAccess = Object.values(lessonAccess).some(l => l.accessible);
      setHasSubscription(hasAccess);
      setCheckingSubscription(false);

      // Find the lesson in all courses
      const coursesRes = await axios.get(`${API}/courses`, { withCredentials: true });
      
      for (const courseData of coursesRes.data) {
        const lessonsRes = await axios.get(`${API}/courses/${courseData.id}/lessons`, { withCredentials: true });
        const foundLesson = lessonsRes.data.find(l => l.id === lessonId);
        
        if (foundLesson) {
          setLesson(foundLesson);
          setCourse(courseData);
          
          // If user has subscription, redirect to actual teaching page
          if (hasAccess) {
            // Determine course type and lesson number for redirect
            const isAICourse = courseData.subject === 'AI' || courseData.name?.includes('AI');
            const isCodingCourse = courseData.subject === 'Coding' || courseData.name?.includes('Coding');
            const lessonNum = foundLesson.lesson_number || foundLesson.week_number;
            
            if (isAICourse) {
              // AI Course redirects
              const aiRoutes = {
                1: '/talk-with-geni',
                2: '/talk-with-geni-week2',
                3: '/week3-lesson',
                4: '/week4-lesson',
                5: '/week5-lesson',
                6: '/week6-lesson',
                7: '/week7-lesson',
                8: '/week8-lesson',
                9: '/week9-lesson',
                10: '/week10-lesson'
              };
              if (aiRoutes[lessonNum]) {
                navigate(aiRoutes[lessonNum], { replace: true });
                return;
              }
            } else if (isCodingCourse) {
              // Coding Course redirects
              const codingRoutes = {
                1: '/coding-lesson-1',
                2: '/coding-lesson-2',
                3: '/coding-lesson-3',
                4: '/coding-lesson-4',
                5: '/coding-lesson-5',
                6: '/coding-lesson-6',
                7: '/coding-lesson-7',
                8: '/coding-lesson-8',
                9: '/coding-lesson-9',
                10: '/coding-lesson-10'
              };
              if (codingRoutes[lessonNum]) {
                navigate(codingRoutes[lessonNum], { replace: true });
                return;
              }
            }
          }
          
          break;
        }
      }

      // Check if already completed
      const progressRes = await axios.get(`${API}/progress`, { withCredentials: true });
      const lessonProgress = progressRes.data.find(p => p.lesson_id === lessonId);
      if (lessonProgress?.completed) {
        setCompleted(true);
      }
    } catch (error) {
      console.error("Failed to load lesson:", error);
      setCheckingSubscription(false);
      // If error checking subscription, assume no subscription
      setHasSubscription(false);
    } finally {
      setLoading(false);
    }
  };

  const startVoiceChat = async () => {
    try {
      setIsVoiceActive(true);
      toast.success("AI Teacher is ready to speak with you!");
      
      // Initialize WebRTC connection
      const RealtimeAudioChat = (await import('@/utils/realtimeAudio')).default;
      audioChat.current = new RealtimeAudioChat();
      await audioChat.current.init();
    } catch (error) {
      console.error("Failed to start voice chat:", error);
      toast.error("Failed to connect to AI teacher");
      setIsVoiceActive(false);
    }
  };

  const stopVoiceChat = () => {
    if (audioChat.current) {
      audioChat.current.disconnect();
      audioChat.current = null;
    }
    setIsVoiceActive(false);
    toast.info("Voice chat ended");
  };

  const markComplete = async () => {
    try {
      await axios.post(
        `${API}/progress/update`,
        {
          lesson_id: lessonId,
          course_id: course.id,
          completed: true,
          progress_percentage: 100
        },
        { withCredentials: true }
      );
      setCompleted(true);
      toast.success("Lesson completed! Great job! 🎉");
    } catch (error) {
      console.error("Failed to mark complete:", error);
      toast.error("Failed to update progress");
    }
  };

  if (loading || checkingSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading lesson...</p>
        </div>
      </div>
    );
  }

  // If user has subscription but we're still here, show lesson content
  // This shouldn't happen normally as we redirect above, but just in case
  if (hasSubscription) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <header className="glass-effect">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <Button
              onClick={() => navigate(`/course/${course?.id}`)}
              variant="ghost"
              className="hover:bg-white/50"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Course
            </Button>
          </div>
        </header>
        <main className="container mx-auto max-w-5xl px-6 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Redirecting to lesson...</p>
          </div>
        </main>
      </div>
    );
  }

  // Show subscription wall only if user doesn't have subscription
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="glass-effect">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Button
            data-testid="back-to-course-btn"
            onClick={() => navigate(`/course/${course?.id}`)}
            variant="ghost"
            className="hover:bg-white/50"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Course
          </Button>
          {completed && (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Completed</span>
            </div>
          )}
        </div>
      </header>

      <main className="container mx-auto max-w-5xl px-6 py-12">
        {/* Lesson Header */}
        <div className="mb-8 animate-slide-in-up">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center justify-center px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">
              Lesson {lesson?.lesson_number}
            </span>
            <span className="text-sm text-gray-600">{course?.name}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">{lesson?.title}</h1>
          <p className="text-base text-gray-600">{lesson?.description}</p>
        </div>

        {/* 🔒 SUBSCRIPTION REQUIRED - Payment Gate */}
        <Card className="border-4 border-yellow-400 shadow-2xl mb-8 bg-gradient-to-br from-yellow-50 to-orange-50">
          <CardContent className="p-12 text-center">
            <div className="mb-6">
              <div className="w-24 h-24 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-5xl">🔒</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Lesson {lesson?.lesson_number} - {lesson?.title}
              </h2>
              <p className="text-xl text-gray-700 mb-6">
                Subscribe to unlock full access to all 10 weeks of interactive lessons!
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 mb-8 shadow-lg max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                🎓 What You'll Get with Subscription:
              </h3>
              <div className="grid md:grid-cols-2 gap-6 text-left">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <p className="font-semibold text-gray-900">Interactive Teaching</p>
                    <p className="text-sm text-gray-600">Geni teaches you step-by-step with voice interaction</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <p className="font-semibold text-gray-900">All 10 Lessons</p>
                    <p className="text-sm text-gray-600">Complete AI & Coding curriculum (Weeks 1-10)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <p className="font-semibold text-gray-900">PDF Downloads</p>
                    <p className="text-sm text-gray-600">Downloadable student books for each week</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <p className="font-semibold text-gray-900">Ask Questions</p>
                    <p className="text-sm text-gray-600">Interrupt and ask Geni anything anytime</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                data-testid="subscribe-to-unlock-btn"
                onClick={() => navigate('/checkout')}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-12 py-8 text-xl font-bold rounded-full shadow-2xl transition-all duration-300 hover:scale-110"
              >
                🎓 Subscribe Now to Unlock All Lessons
              </Button>
              <Button
                onClick={() => navigate('/demo-chat')}
                variant="outline"
                className="border-2 border-yellow-500 text-yellow-700 hover:bg-yellow-50 px-8 py-6 text-lg font-semibold rounded-full"
              >
                💬 Try Free Demo First
              </Button>
            </div>

            <p className="text-sm text-gray-600 mt-6">
              Want to see how it works? Try our <button onClick={() => navigate('/demo-chat')} className="text-blue-600 underline font-semibold">free demo chat</button> first!
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LessonView;
